import { useState, type FormEvent } from "react";

import { useAuthStore } from "../state/useAuthStore";

/**
 * The whole app when there is no session. Nothing else is mounted behind it —
 * the map, the panels and every query they make only exist once the server has
 * said yes, so an anonymous browser never even asks for a tile.
 */
export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const configured = useAuthStore((s) => s.configured);
  const [passphrase, setPassphrase] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !passphrase) return;
    setBusy(true);
    setError(null);
    const result = await login(passphrase);
    if (!result.ok) {
      setError(result.message);
      // Clear the box on failure: retyping is cheap, and a wrong passphrase
      // left sitting in the field is the thing most likely to be resubmitted.
      setPassphrase("");
    }
    setBusy(false);
  }

  return (
    <main className="flex h-full w-full items-center justify-center bg-surface-alt px-6">
      <div className="w-full max-w-[22rem]">
        <div className="flex flex-col items-center">
          {/* Rendered from the 512px master at 112px, so it stays sharp on
              high-density displays without shipping the 2048px original. */}
          <img
            src="/logo-512.png"
            alt="meteo.immo"
            width={512}
            height={512}
            decoding="async"
            className="h-100 w-100 rounded-2xl"
          />
          <h1 className="mt-4 text-lg font-semibold tracking-tight">
            meteo.immo
          </h1>
          <p className="mt-1 text-[12px] text-ink-soft">
            Île-de-France flat explorer
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-7 rounded-xl border border-line bg-surface p-5 shadow-sm"
        >
          <label
            htmlFor="passphrase"
            className="block text-[12px] font-medium text-ink-soft"
          >
            Passphrase
          </label>
          <input
            id="passphrase"
            name="passphrase"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "passphrase-error" : undefined}
            className="mt-1.5 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/25"
          />

          <button
            type="submit"
            disabled={busy || !passphrase}
            className="mt-3 w-full rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {busy ? "Checking…" : "Enter"}
          </button>

          {error && (
            <p
              id="passphrase-error"
              role="alert"
              className="mt-3 text-[12px] text-red-600"
            >
              {error}
            </p>
          )}
          {!configured && (
            <p className="mt-3 text-[12px] text-ink-soft">
              No passphrase is configured on the server. Set{" "}
              <code className="font-mono">METE_PASSPHRASE</code> in{" "}
              <code className="font-mono">.env</code> and restart the API.
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
